<template>
  <!--
    Keep a single element root. Root `v-if` SSRs a comment when empty, which
    breaks Lazy + hydrate-on-visible (IntersectionObserver needs an Element).
  -->
  <div>
    <EventIncluded
      v-if="shouldShowItem('included')"
      :included="structuredIncluded"
      :event-data="eventData"
      :is-giftcard="isGiftcard"
    />

    <div
      v-if="visibleFaqItems.length"
      itemscope
      itemtype="https://schema.org/FAQPage"
    >
      <div
        v-for="ok in visibleFaqItems"
        :key="ok.type"
        :data-id="ok.type"
        class="border-t border-gray-200 py-7"
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <div class="flex items-start">
          <component
            :is="ok.icon"
            class="mt-0.5 size-6 shrink-0 text-teal-600"
            aria-hidden="true"
          />
          <div class="ml-4 min-w-0">
            <h3
              class="text-base font-semibold leading-6 text-stone-800"
              itemprop="name"
            >
              {{ ok.title }}
            </h3>
          </div>
        </div>

        <div
          class="mt-4 text-sm leading-4 text-stone-600"
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <HtmlReadMore
            :html="ok.description"
            :default-expanded="ok.type === 'included' && isBoxEvent"
            :test-id-prefix="`event-important-details-${ok.type}`"
            itemprop="text"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  FileText,
  MapPinCheckInside,
  PencilRuler,
  UserCheck,
  Users,
} from 'lucide-vue-next'
import { useTitleByCategory } from '~/composables/product/useGetTitleByCategory'
import type { EventDescription } from '~/composables/types/event'
import {
  EVENT_IMPORTANT_DETAIL_ALL_ITEMS,
  type EventImportantDetailSlug,
} from '~/utils/eventImportantDetails'

interface ImportantDetailItem {
  type: EventImportantDetailSlug
  icon: Component
  title: string
  description: string
}

const { t: $t } = useI18n()
const { getTitleByCategory } = useTitleByCategory()

const {
  eventData = {} as EventDescription,
  isGiftcard = false,
  items = EVENT_IMPORTANT_DETAIL_ALL_ITEMS,
} = defineProps<{
  eventData?: EventDescription
  isGiftcard?: boolean
  /** Slugs of sections to render, in display order. Defaults to all. */
  items?: EventImportantDetailSlug[]
}>()

const isBoxEvent = computed(() => eventGetters.isBoxEvent(eventData))

const structuredIncluded = computed(() => eventGetters.getIncluded(eventData))
const hasStructuredIncluded = computed(
  () => structuredIncluded.value.length > 0
)

const allowedItems = computed(() => new Set(items))

const shouldShowItem = (slug: EventImportantDetailSlug) =>
  allowedItems.value.has(slug)

/** API rich-text fields are typed string but may be null/non-string at runtime. */
const asHtmlDescription = (value: unknown): string =>
  typeof value === 'string' ? value : ''

const allFaqItems = computed<ImportantDetailItem[]>(() => {
  const candidates: ImportantDetailItem[] = [
    ...(!hasStructuredIncluded.value
      ? [
          {
            type: 'included' as const,
            icon: PencilRuler,
            title: getTitleByCategory('whatIsIncluded', eventData, isGiftcard),
            description: asHtmlDescription(
              eventGetters.getWhatIsIncluded(eventData)
            ),
          },
        ]
      : []),
    {
      type: 'bring',
      icon: FileText,
      title: getTitleByCategory('whatToBring', eventData, isGiftcard),
      description: asHtmlDescription(eventGetters.getWhatToBring(eventData)),
    },
    {
      type: 'people',
      icon: Users,
      title: getTitleByCategory('teamEventAndPrivate', eventData, isGiftcard),
      description: asHtmlDescription(
        eventGetters.getTeamEventsAndGiftCardsSummary(eventData) ||
          $t('product.show.giftcardIcons.teamEventsAndGiftCardsContent')
      ),
    },
    ...(asHtmlDescription(eventGetters.getLocationSummary(eventData))
      ? [
          {
            type: 'locationSummary' as const,
            icon: MapPinCheckInside,
            title: getTitleByCategory('locationSummary', eventData, isGiftcard),
            description: asHtmlDescription(
              eventGetters.getLocationSummary(eventData)
            ),
          },
        ]
      : []),
    ...(asHtmlDescription(eventGetters.getCourseTarget(eventData))
      ? [
          {
            type: 'courseTarget' as const,
            icon: UserCheck,
            title: getTitleByCategory('courseTarget', eventData, isGiftcard),
            description: asHtmlDescription(
              eventGetters.getCourseTarget(eventData)
            ),
          },
        ]
      : []),
  ]

  return candidates.filter((item) => item.description.trim().length > 0)
})

const visibleFaqItems = computed(() => {
  const byType = new Map(
    allFaqItems.value.map((item) => [item.type, item] as const)
  )

  return items
    .map((slug) => byType.get(slug))
    .filter((item): item is ImportantDetailItem => Boolean(item))
})
</script>
